import React, { useState, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useCookies } from 'react-cookie'
import './Login.css'
import Loader from './../loader/Loader'
import { useAuth } from '../../auth/Auth'
import { usePassword } from '../../auth/PasswordContext'
import { useLandAfterSignIn } from '../../auth/useLandAfterSignIn'
import { GpAuthShell, GpButton, GpFormField, Icon } from '../../components/gp'

const iconBtn = {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--gp-ink-mute)',
    display: 'inline-flex',
    padding: 4,
}

const linkStyle = {
    color: 'var(--gp-brand)',
    textDecoration: 'none',
    fontWeight: 500,
    cursor: 'pointer',
}

const Login = () => {
    const [email, setEmail] = useState('')
    const { password, setPassword, clearPassword } = usePassword()
    const [isLoading, setIsLoading] = useState(false)
    const [wrongPassword, setWrongPassword] = useState(false)
    const [somethingIsWrong, setSomethingIsWrong] = useState(false)
    const [accountDisabled, setAccountDisabled] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [cookies, setCookie, removeCookie] = useCookies(['gopowerev', 'inviteUuid', 'inviteRole'])

    const history = useHistory()
    const location = useLocation()
    const auth = useAuth()
    const landAfterSignIn = useLandAfterSignIn()

    const goToSignUp = () => history.push('/sign-up')
    const goToReset = () => history.push('/forgot-password')

    const logIn = async () => {
        setSomethingIsWrong(false)
        setAccountDisabled(false)
        setWrongPassword(false)
        setIsLoading(true)
        setCookie('userEmail', email, { path: '/' })
        const validationData = {
            inviteUuid: location.state?.inviteUuid || cookies?.inviteUuid || '',
        }
        auth.signin(email, password, validationData)
            .then(async (user) => {
                setEmail('')
                if (!user.attributes?.email_verified) {
                    setCookie('userConfirmed', false, { path: '/' })
                    history.push('/sign-up/codeValidation')
                } else {
                    clearPassword()
                    // Decides from the invite cookie the render captured, so clearing the
                    // cookies right after cannot change where this sign-in lands.
                    landAfterSignIn(user)
                    removeCookie('inviteUuid', { path: '/' })
                    removeCookie('inviteRole', { path: '/' })
                }
            })
            .catch((error) => {
                if (error.code === 'NotAuthorizedException') {
                    setWrongPassword(true)
                } else if (error.code === 'UserNotConfirmedException') {
                    setCookie('userConfirmed', false, { path: '/' })
                    goToSignUp()
                } else if (error.code === 'UserLambdaValidationException') {
                    setAccountDisabled(true)
                } else {
                    setSomethingIsWrong(true)
                }
            })
            .finally(() => setIsLoading(false))
    }

    useEffect(() => {
        setCookie('userConfirmed', undefined, { path: '/' })
        document.querySelector('body').scrollTo(0, 0)
        if (cookies.userEmail) {
            setEmail(cookies.userEmail)
        }
    }, [])

    const canSubmit = email.length > 0 && password.length > 0 && !isLoading
    const hasError = wrongPassword || somethingIsWrong || accountDisabled

    const renderError = () => {
        if (!hasError) return null
        let text
        if (wrongPassword) text = 'Incorrect email or password.'
        else if (accountDisabled)
            text = (
                <>
                    Your account has been disabled. Contact{' '}
                    <a href="mailto:support@gopowerev.com" style={linkStyle}>
                        support@gopowerev.com
                    </a>
                    .
                </>
            )
        else text = 'Oops, something went wrong. Please wait and try again.'
        return (
            <div
                style={{
                    font: 'var(--gp-caption)',
                    color: 'var(--gp-danger)',
                    marginTop: 8,
                }}
            >
                {text}
            </div>
        )
    }

    return (
        <>
            {isLoading && <Loader />}
            <GpAuthShell>
                <h2
                    data-testid="cypress-sign-in-header"
                    style={{
                        font: '500 32px/1.1 var(--gp-font)',
                        letterSpacing: -0.5,
                        margin: '0 0 6px',
                        color: 'var(--gp-ink)',
                    }}
                >
                    Sign in
                </h2>
                <p
                    data-testid="cypress-welcome-message"
                    style={{
                        font: 'var(--gp-body)',
                        color: 'var(--gp-ink-mute)',
                        margin: '0 0 28px',
                    }}
                >
                    Welcome back!
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <GpFormField
                        label="Email"
                        icon="mail"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        autoCapitalize="none"
                        autoComplete="email"
                        data-testid="cypress-sign-in-email"
                    />

                    <div>
                        <GpFormField
                            label="Password"
                            icon="lock"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            autoComplete="current-password"
                            data-testid="cypress-sign-in-password"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && canSubmit) logIn()
                            }}
                            trailing={
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((v) => !v)}
                                    aria-label={
                                        showPassword
                                            ? 'Hide password'
                                            : 'Show password'
                                    }
                                    style={iconBtn}
                                >
                                    <Icon
                                        name={showPassword ? 'eyeOff' : 'eye'}
                                        size={18}
                                        stroke={1.75}
                                    />
                                </button>
                            }
                        />
                        {renderError()}
                    </div>

                    <div style={{ marginTop: 8 }}>
                        <GpButton
                            variant="primary"
                            onClick={logIn}
                            disabled={!canSubmit}
                            data-testid="cypress-sign-in-button"
                            style={{ width: '100%' }}
                        >
                            {isLoading ? 'Signing in…' : 'Sign in'}
                        </GpButton>
                    </div>

                    <div
                        style={{
                            font: 'var(--gp-caption)',
                            color: 'var(--gp-ink-mute)',
                            textAlign: 'center',
                            marginTop: 4,
                        }}
                    >
                        Trouble signing in?{' '}
                        <span onClick={goToReset} style={linkStyle}>
                            Reset your password
                        </span>
                    </div>
                </div>

                <div
                    style={{
                        margin: '28px 0 0',
                        height: 1,
                        background:
                            'repeating-linear-gradient(to right, var(--gp-hairline) 0, var(--gp-hairline) 4px, transparent 4px, transparent 8px)',
                    }}
                />

                <div
                    style={{
                        marginTop: 20,
                        font: 'var(--gp-body)',
                        color: 'var(--gp-ink-mute)',
                        textAlign: 'center',
                    }}
                >
                    Don't have an account?{' '}
                    <span onClick={goToSignUp} style={linkStyle}>
                        Sign up
                    </span>
                </div>
            </GpAuthShell>
        </>
    )
}

export default Login
